import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { ProductList } from "@/components/ProductList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Inventory } from "@/components/Inventory";
import { CustomerList } from "@/components/customers/CustomerList";
import { DocumentList } from "@/components/documents/DocumentList";
import { DocumentForm } from "@/components/documents/DocumentForm";
import { Header } from "@/components/Header";
import { LoginForm } from "@/components/auth/LoginForm";
import { AuthProvider, useAuth } from "@/components/auth/AuthContext";
import { ColorSettingsProvider } from "@/contexts/ColorSettingsContext";
import { Settings } from "@/components/settings/Settings";
import { OrderList } from "@/components/orders/OrderList";
import { OrderForm } from "@/components/orders/OrderForm";
import { PendingOrders } from "@/components/orders/PendingOrders";
import { Chat } from "@/components/chat/Chat";
import { Informes } from "@/components/informes/Informes";
import { DashboardRepresentante } from "@/components/informes/DashboardRepresentante";

function AppContent() {
  const [hash, setHash] = React.useState(window.location.hash);
  const { user, hasPermission } = useAuth();

  React.useEffect(() => {
    const handleHashChange = () => {
      setHash(window.location.hash);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (!user) {
    return <LoginForm />;
  }

  const renderContent = () => {
    if (hash === '#/chat') {
      return <Chat />;
    }
    if (hash === '#/informes') {
      return <Informes />;
    }
    if (hash.startsWith('#/dashboard-representante/')) {
      const codRep = hash.replace('#/dashboard-representante/', '');
      return <DashboardRepresentante codRep={codRep} />;
    }
    if (hash.startsWith('#/entrada/')) {
      if (!hasPermission("entrada")) return <div>Acceso denegado</div>;
      const id = hash.replace('#/entrada/', '');
      return <DocumentForm type="entrada" documentId={id === 'new' ? null : id} onClose={() => window.location.hash = '#/entradas'} />;
    }
    if (hash.startsWith('#/salida/')) {
      if (!hasPermission("salida")) return <div>Acceso denegado</div>;
      const id = hash.replace('#/salida/', '');
      return <DocumentForm type="salida" documentId={id === 'new' ? null : id} onClose={() => window.location.hash = '#/salidas'} />;
    }
    if (hash.startsWith('#/pedido/')) {
      const id = hash.replace('#/pedido/', '');
      return <OrderForm orderId={id === 'new' ? null : id} onClose={() => window.location.hash = '#/pedidos'} />;
    }
    
    return (
      <Tabs defaultValue={hash.replace('#/', '') || 'products'} className="w-full">
        <TabsList className="grid w-full grid-cols-7 mb-8">
          <TabsTrigger value="products" onClick={() => window.location.hash = '#/products'}>Productos</TabsTrigger>
          <TabsTrigger value="customers" onClick={() => window.location.hash = '#/customers'}>Clientes</TabsTrigger>
          {hasPermission("entrada") && <TabsTrigger value="entradas" onClick={() => window.location.hash = '#/entradas'}>Entradas</TabsTrigger>}
          {hasPermission("salida") && <TabsTrigger value="salidas" onClick={() => window.location.hash = '#/salidas'}>Salidas</TabsTrigger>}
          <TabsTrigger value="pedidos" onClick={() => window.location.hash = '#/pedidos'}>Pedidos</TabsTrigger>
          {(user.role === 'admin' || user.ver_comisiones_globales) && (
            <TabsTrigger value="inventory" onClick={() => window.location.hash = '#/inventory'}>Inventario</TabsTrigger>
          )}
          {hasPermission("configuracion") && <TabsTrigger value="settings" onClick={() => window.location.hash = '#/settings'}>Configuración</TabsTrigger>}
        </TabsList>
        <TabsContent value="products">
          <ProductList readOnly={!hasPermission("productos_gestion")} />
        </TabsContent>
        <TabsContent value="customers">
          <CustomerList readOnly={!hasPermission("clientes_gestion")} />
        </TabsContent>
        {hasPermission("entrada") && (
          <TabsContent value="entradas">
            <DocumentList type="entrada" />
          </TabsContent>
        )}
        {hasPermission("salida") && (
          <TabsContent value="salidas">
            <DocumentList type="salida" />
          </TabsContent>
        )}
        <TabsContent value="pedidos">
          <OrderList />
        </TabsContent>
        {(user.role === 'admin' || user.ver_comisiones_globales) && (
          <TabsContent value="inventory">
            <Inventory />
          </TabsContent>
        )}
        {hasPermission("configuracion") && (
          <TabsContent value="settings">
            <Settings />
          </TabsContent>
        )}
      </Tabs>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <Header />
      <main className="container mx-auto p-4">
        {renderContent()}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <>
      <Toaster />
      <AuthProvider>
        <ColorSettingsProvider>
          <AppContent />
        </ColorSettingsProvider>
      </AuthProvider>
    </>
  );
}
